import { detectedTextTranslation } from '@/common/preview/isolated-glyph-alignment';
import { v4 as uuid } from "uuid";
import {cleanImageResourceUrl, getOrderForAddField, getSettingsDataInstance} from '../../../utils';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { canvasEditModeControl } from "@frontend/group/modules/canvas-edit-mode-control";
import {
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID, ADDITIONAL_CIRCLE_PREFIX_ID,
  ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID,
  ADDITIONAL_RECTANGLE_PREFIX_ID,
  ADDITIONAL_SAFE_ZONE_PREFIX_ID,
  ADDITIONAL_TEXT_PREFIX_ID,
  ADDITIONAL_LINE_PREFIX_ID,
  HORIZONTAL_GUIDE_LINE_PREFIX_ID,
  VERTICAL_GUIDE_LINE_PREFIX_ID,
  ADDITIONAL_CUT_LINE_PREFIX_ID, ADDITIONAL_SMART_OBJECT_PREFIX_ID,
  IMAGE_PREFIX_ID,
} from "@frontend/constants/templates-field-prefixes";
import TemplateService from '@frontend/services/api/template'
import {FIELD_TYPE_ADDITIONAL_FIELDS, FIELD_TYPE_SMART_OBJECT} from "@frontend/constants/type-fields-of-template";
import { NON_USER_LAYER_IDS } from "@/common/preview/positioning-lines";
import { EVENTS, iconsSettingsEventBroker } from "@frontend/group/modules/icons-settings/event-broker";
import { simpleTableBuilderEventBroker, EVENTS as Table_EVENTS } from "@frontend/group/modules/simple-table-builder/event-broker";
import BuilderTable from "@frontend/group/modules/simple-table-builder/builder";
import { parseJsonData } from "@frontend/services/helpers";
import {initAdditionalBackgroundThemeImage} from "@/common/preview/draw-previews";
import {setAdditionalBackgroundImage} from "@/common/preview/set-additional-background-image";
import {drawSmartObject} from "@/common/preview/draw-smart-object";
import {ORIENTATION} from "@frontend/constants/orientation";
import {getAllObjects} from "@frontend/services/helpers/group-banner-helpers";
import { getComp } from '@frontend/group/modules/comps';
import { getCompGeometry } from '@frontend/group/modules/comps/geometry';

const getShowPageScope = () => ShowPageScopeCollector.get([
  'previews',
  'drawPreviews',
  'sliceForCurrentPage',
  'canvas_data',
  'getChangesLayoutById'
]);

const getPreview = () => {
  const { previews = [] } = getShowPageScope() ?? {};

  return previews;
}

const hasOwnOption = (options, key) => Object.prototype.hasOwnProperty.call(options, key);

function applyCompDefaults(type, options) {
  const preview = options.currentPreview;
  const comp = getComp(preview);
  if (!comp || !['Text', 'Multi Line Text', 'Rectangle', 'Circle'].includes(type)) return;

  const fieldDefaults = {
    Text: DEFAULT_TEXT_FIELD_PROPERTIES,
    'Multi Line Text': DEFAULT_MULTI_LINE_TEXT_FIELD_PROPERTIES,
    Rectangle: DEFAULT_RECTANGLE_FIELD_PROPERTIES,
    Circle: DEFAULT_CIRCLE_FIELD_PROPERTIES
  }[type];
  const area = getCompGeometry(preview.dimension[0], preview.dimension[1], comp).worldComp;
  const padding = Math.min(20, Math.floor(Math.min(area.width, area.height) / 4));
  const availableWidth = Math.max(1, area.width - padding * 2);
  const availableHeight = Math.max(1, area.height - padding * 2);
  const defaults = {
    _offset_x: area.left + padding,
    _offset_y: area.top + padding,
    _width: Math.min(Math.round(Number(fieldDefaults._width)), availableWidth),
    _height: Math.min(Math.round(Number(type === 'Text' ? fieldDefaults._b_height : fieldDefaults._height)), availableHeight)
  };
  if (type === 'Text' || type === 'Multi Line Text') {
    defaults._fontsize = Math.max(10, Math.min(100, Math.floor(availableHeight / 2), Math.floor(availableWidth / 6)));
  }
  Object.entries(defaults).forEach(([key, value]) => {
    if (!hasOwnOption(options, key)) options[key] = String(value);
  });
}



let canvasData,
  drawFieldsFunctions,
  updateLayoutChanges,
  drawPreviews,
  previews,
  order = 0,
  reorderObjects;

export const DEFAULT_FONT_FOR_ADDITION_TEXT_FIELD = 'AlternateGothicATF-Black';

export const DEFAULT_TEXT_FIELD_PROPERTIES = {
  _alignment: "left",
  _angle: "0.00",
  _b_height: "114",
  _b_width: "186.49969100952148",
  _bounding_rect_data: '{"left":136,"top":57,"width":186,"height":114}',
  _color: "#000",
  _font: DEFAULT_FONT_FOR_ADDITION_TEXT_FIELD,
  _fontsize: "100",
  _height: "0",
  _offset_x: "136.00",
  _offset_y: "57.00",
  _order: "0",
  _shadow: "0",
  _skew_coords: '"[]"',
  _skew_image: "",
  _skew_image_point: "",
  _topmost_y: "19",
  _tracking: 0,
  _width: "185",
  _translated_from_element_id: null,
  _allow_empty_string: false,
  _max_chars: 10000,
};

export const DEFAULT_MULTI_LINE_TEXT_FIELD_PROPERTIES = {
  _alignment: "left",
  _angle: "0.00",
  _bounding_data: '{"left":136,"top":57.00000000000001,"width":735.0987739562988,"height":114}',
  _color: "#000",
  _corner_radius: "",
  _corners: "",
  _font: DEFAULT_FONT_FOR_ADDITION_TEXT_FIELD,
  _fontsize: "100",
  _height: "112.99999999999999",
  _leading: "1",
  _line_heights: "113.00",
  _offset_x: "136.00",
  _offset_y: "57.00",
  _opacity: "1",
  _order: "0",
  _radius: "",
  _shadow: "0",
  _skew_coords: '"[]"',
  _skew_image: "",
  _skew_image_point: "",
  _thickness: "",
  _topmost_y: '[{"topMostX":14,"topMostY":16}]',
  _tracking: 0,
  _width: "734.0987739562988",
  _translated_from_element_id: null,
  _styles: {},
  _max_chars: 10000,
};
export const DEFAULT_RECTANGLE_FIELD_PROPERTIES = {
  _angle: "0",
  _corner_radius: "0",
  _corners: "[0,0,0,0]",
  _fill_color: "#000000",
  _height: "300",
  _offset_x: "0",
  _offset_y: "0",
  _opacity: "1",
  _order: "88",
  _radius: "150",
  _scaleX: "1",
  _scaleY: "1",
  _stroke_color: "#000",
  _thickness: "0",
  _toggle_shape: "on",
  _width: "300",
  _fill_type: "solid",
  _gradient: null,
};
export const DEFAULT_CIRCLE_FIELD_PROPERTIES = {
  _angle: "0",
  _corner_radius: "0",
  _corners: "[0,0,0,0]",
  _fill_color: "#000000",
  _height: "300",
  _offset_x: "0",
  _offset_y: "0",
  _opacity: "1",
  _order: "88",
  _radius: "150",
  _scaleX: "1",
  _scaleY: "1",
  _stroke_color: "#000",
  _thickness: "0",
  _toggle_shape: "on",
  _width: "300",
  _fill_type: "solid",
  _gradient: null,
};
export const DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES = {
  'additional_background_images': '/img/icons/image.png',
  "add_bk_img_offset_x": 300,
  "add_bk_img_offset_y": 240,
  "add_bk_img_scale": 1,
  "add_bk_img_scale_y": 1,
  "add_bk_img_shadow": 0,
  "add_bk_img_angle": 0,
  "add_bk_img_is_mirror": false,
  "add_bk_img_mirror_position": "right",
  "add_bk_img_mirror_distance": 0,
  "add_bk_img_mirror_coords": "{}",
  "add_bk_img_mirror_skew": 0,
  "add_bk_img_mirror_delete_source": false,
  "add_bk_img_mirror_fade": false,
  "add_bk_img_order": 150,
  "add_bk_img_duplicate_data": null,
  "add_bk_img_width": 520,
  "add_bk_img_height": 400,
  "add_bk_img_flipX": 0,
  "add_bk_img_flipY": 0,
  "add_bk_img_masks": [],
  "add_bk_img_is_shifted": [],
  "add_bk_img_opacity": 1,
  "add_bk_img_custom_shadow": null,
  "add_bk_img_custom_outline_shadow": null,
  "add_bk_img_mirror_shadows": [],
  "add_bk_img_oval_shadows": [],
};
export const DEFAULT_SAFE_ZONE_FIELD_PROPERTIES = {
  _fill_color: "#000000",
  _height: "200",
  _offset_x: "0",
  _offset_y: "0",
  _opacity: "0.2",
  _order: "99999",
  _stroke_color: "#000",
  _thickness: "0",
  _width: "200",
}

export const DEFAULT_ICONS_FIELD_PROPERTIES = {
  top: 0,
  left: 0,
  height: 100,
  width: 100,
  order: 1000,
}

export const DEFAULT_LINE_FIELD_PROPERTIES = {
  _stroke_color: "#000000",
  _thickness: 5,
  _offset_x: 0,
  _offset_y: 0,
  _opacity: 1,
  _order: 99999,
  _coords: [0, 0, 500, 0],
  _angle: 0,
}

export const DEFAULT_SMART_OBJECT_FIELD_PROPERTIES = {
  _width: 200,
  _height: 200,
  _x: 100,
  _y: 100,
  _group_name: 'UniqueName', //required, unique per layout
  _additional_members: [],
  _options: JSON.stringify({
    mask: {
      radius: 0,
    },
    shadow: {
      angle: 80,
      distance: 10,
      opacity: 80,
      size: 40,
      enabled: false
    },
    skew: {
      render: false,
      value: [0,0,0,0,0,0,0,0]
    },
    showBackground: true
  }),
}

export const DEFAULT_STATIC_TEXT_FIELD_PROPERTIES = {
  _alignment: "left",
  _angle: "0.00",
  _color: "#000",
  _font: DEFAULT_FONT_FOR_ADDITION_TEXT_FIELD,
  _fontsize: "100",
  _height: "100",
  _offset_x: "136.00",
  _offset_y: "57.00",
  _order: "0",
  _shadow: "0",
  _width: "185",
  _opacity: "1",
}

export const DEFAULT_STATIC_IMAGE_FIELD_PROPERTIES = {
  _width: "200",
  _height: "200",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "1",
  _angle: "0.00",
}

export const DEFAULT_UPLOAD_IMAGE_FIELD_PROPERTIES = {
  _width: "200",
  _height: "200",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "1",
  _angle: "0.00",
}

export const DEFAULT_IMAGE_LIST_FIELD_PROPERTIES = {
  _width: "200",
  _height: "200",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "1",
  _angle: "0.00",
}

export const DEFAULT_HTML_FIELD_PROPERTIES = {
  _width: "300",
  _height: "200",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "1",
  _angle: "0.00",
  _html_content: "<div>HTML Content</div>",
}

export const DEFAULT_UPC_GTIN_FIELD_PROPERTIES = {
  _width: "200",
  _height: "100",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "1",
  _angle: "0.00",
  _barcode_value: "123456789012",
}

export const DEFAULT_PRODUCT_DIMENSIONS_FIELD_PROPERTIES = {
  _width: "200",
  _height: "100",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "1",
  _angle: "0.00",
  _dimensions_text: "10\" x 8\"",
}

export const DEFAULT_OUTLINE_FIELD_PROPERTIES = {
  _stroke_color: "#000000",
  _thickness: 2,
  _offset_x: 0,
  _offset_y: 0,
  _opacity: 1,
  _order: 99999,
  _coords: [0, 0, 500, 0],
}

export const DEFAULT_TEMPLATE_GUIDELINES_FIELD_PROPERTIES = {
  _width: "200",
  _height: "200",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "0.5",
  _angle: "0.00",
  _guideline_type: "safe_zone",
}

export const DEFAULT_LIST_FIELD_PROPERTIES = {
  _width: "200",
  _height: "100",
  _offset_x: "0",
  _offset_y: "0",
  _order: "0",
  _opacity: "1",
  _angle: "0.00",
  _list_items: ["Item 1", "Item 2", "Item 3"],
  _list_style: "bullet",
}

export const DEFAULT_CUT_LINE_PROPERTIES = {
  _offset_x: 0,
  _thickness: 5,
  _stroke_color: "#000000",
  _opacity: 1,
  _orientation: ORIENTATION.HORIZONTAL
}

function getFieldName(field_type) {
  return "add_" + field_type.toLowerCase().replace(/ /g, '_') + "_" + Date.now();
}

function applyShadowProperties(options, fieldName, instanceId, prefix = '') {
  const shadowTypes = ['shadow', 'outlineShadow', 'mirrorShadow', 'ovalShadow'];

  const shadowKeys = {
    shadow: 'custom_shadow',
    outlineShadow: 'custom_outline_shadow',
    mirrorShadow: 'custom_mirror_shadow',
    ovalShadow: 'custom_oval_shadow'
  };

  shadowTypes.forEach(type => {
    if (options[type]) {
      const shadowValue = typeof options[type] === 'string' ? options[type] : JSON.stringify(options[type]);
      const key = prefix ? `${prefix}_${shadowKeys[type]}` : `${fieldName}_${shadowKeys[type]}`;
      updateLayoutChanges(instanceId, key, shadowValue);
    }
  });
}

function moveRulerGuideLinesToFront(canvas) {
  canvas.getObjects()
    .filter(
      obj => [HORIZONTAL_GUIDE_LINE_PREFIX_ID, VERTICAL_GUIDE_LINE_PREFIX_ID]
        .some(id => obj.id.startsWith(id))
    )
    .forEach(obj => obj.bringToFront());
}

function syncCanvasObjectOrders(orderIds = [], canvas) {
  if (!canvas || !Array.isArray(orderIds) || !orderIds.length) return;

  const orderMap = orderIds.reduce((acc, id, index) => {
    acc[id] = index;
    return acc;
  }, {});

  canvas.getObjects().forEach(object => {
    if (!object?.id) return;
    if (Object.hasOwn(orderMap, object.id)) {
      object.order = orderMap[object.id];
    }
  });
}

export function orderHandler(currentRenderedObject, options = {}, params = {}) {
  // accept sendToBack
  const { hasUsersOrder = false, bringToFront = false, sendToBack = false } = params;
  // Parse users_fields_order safely; initialize if missing
  let currentOrder = options.currentPreview.data?.users_fields_order
    ? JSON.parse(options.currentPreview.data.users_fields_order)
    : [];

  if (!hasUsersOrder) return;
  if (!Array.isArray(currentOrder)) {
    currentOrder = [];
  }

  if (!currentOrder.length && options.currentPreview?.canvas) {
    currentOrder = options.currentPreview.canvas.getObjects()
      .filter(object =>
        object.id &&
        !NON_USER_LAYER_IDS.includes(object.id) &&
        !NON_USER_LAYER_IDS.some(prefix => object.id.startsWith(prefix))
      )
      .sort((prev, curr) => prev.order - curr.order)
      .map(object => object.fieldId || object.id);
  }

  // Ensure no duplicates of the same id before re-inserting
  currentOrder = currentOrder.filter(id => id !== currentRenderedObject.id);

  const persistOrder = () => {
    const serializedOrder = JSON.stringify(currentOrder);
    updateLayoutChanges(options.currentInstanceId, 'users_fields_order', serializedOrder);
    syncCanvasObjectOrders(currentOrder, options.currentPreview?.canvas);
  };

  if (bringToFront) {
    // Front = index 0
    currentOrder.unshift(currentRenderedObject.id);
    persistOrder();
    return;
  }

  if (sendToBack) {
    // Back = final position (behind other user layers)
    currentOrder.push(currentRenderedObject.id);
    persistOrder();
    return;
  }

  // Preserve original behavior (insert near originalId, else front)
  if (options.originalId) {
    const originalIndex = currentOrder.indexOf(options.originalId);
    const insertAt = originalIndex >= 0 ? originalIndex : 0;
    currentOrder.splice(insertAt, 0, currentRenderedObject.id);
  } else {
    currentOrder.unshift(currentRenderedObject.id);
  }

  persistOrder();
}

function getNextSmartObjectData(items) {
  const regex = new RegExp(`^${ADDITIONAL_SMART_OBJECT_PREFIX_ID}(\\d+)$`);

  const numbers = items
    .map(item => item.element_id?.match(regex))
    .filter(Boolean)
    .map(m => Number(m[1]));

  const max = numbers.length ? Math.max(...numbers) : -1;
  const nextNumber = max + 1;

  return {
    id: `${ADDITIONAL_SMART_OBJECT_PREFIX_ID}${nextNumber}`,
    number: nextNumber,
    groupName: getNextGroupName(items),
  };
}

function getNextGroupName(items, baseName = "SmartObject") {
  const existing = new Set(items.map(i => i.groupName));

  for (let n = 1; n <= 1000; n++) {
    const name = `${baseName}${n}`;
    if (!existing.has(name)) {
      return name;
    }
  }

  let name;
  do {
    const rand = Math.floor(Math.random() * 1e6);
    name = `${baseName}${rand}`;
  } while (existing.has(name));

  return name;
}

function normalizeConvertedProductImageSmartObjectOffsets({
  convertedObject,
  currentRenderedObject,
  currentInstanceId,
}) {
  if (!convertedObject?.id?.startsWith(IMAGE_PREFIX_ID)) {
    return;
  }

  const index = parseInt(convertedObject.id.split('_').pop());
  const origin = canvasData?.[currentInstanceId]?.originCoords?.[index];

  if (!Number.isInteger(index) || !origin) {
    return;
  }

  const point = convertedObject.getPointByOrigin(
    convertedObject.__originX || convertedObject.originX,
    convertedObject.originY
  );
  const smartObjectOffset = {
    x: currentRenderedObject.left,
    y: currentRenderedObject.top,
  };
  const xOffset = point.x - smartObjectOffset.x - origin.x;
  const yOffset = point.y - smartObjectOffset.y - origin.y;
  const { getChangesLayoutById } = getShowPageScope() ?? {};
  const data = {
    ...canvasData[currentInstanceId]._data,
    ...(getChangesLayoutById?.(currentInstanceId) || {}),
  };
  const xOffsets = [...(data.x_offset || [])];
  const yOffsets = [...(data.y_offset || [])];

  xOffsets[index] = xOffset.toFixed(2);
  yOffsets[index] = yOffset.toFixed(2);

  updateLayoutChanges(currentInstanceId, 'x_offset', xOffsets);
  updateLayoutChanges(currentInstanceId, 'y_offset', yOffsets);

  origin.x += smartObjectOffset.x;
  origin.y += smartObjectOffset.y;
}

function addFieldSettings({
  fieldIndex,
  prefix,
  options,
}) {
  const settingTemplate = getSettingsDataInstance(options.currentPreview?.data?.settings_template);
  // remove previous name from template settings by new id
  settingTemplate.deleteName(`${prefix}${fieldIndex}`);
  updateLayoutChanges(options.currentInstanceId, 'settings_template', settingTemplate.toJSON());
  if (options?.name || options?.link) {
    settingTemplate.addField({
      id: `${prefix}${fieldIndex}`,
      name: options.name || null,
      link: options.link || ''
    });

    updateLayoutChanges(options.currentInstanceId, 'settings_template', settingTemplate.toJSON());
  }
}

export async function addAdditionalText(field_type, options = {}, params = {}) {
  const fieldName = getFieldName(field_type);
  const newData = {};
  newData[fieldName] = options.text || 'text';

  for (let key of Object.keys(DEFAULT_TEXT_FIELD_PROPERTIES)) {
    newData[`${fieldName + key}`] = options[key] || DEFAULT_TEXT_FIELD_PROPERTIES[key];
  }

  canvasData[options.currentInstanceId].additionalTexts.push(fieldName);

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  applyShadowProperties(options, fieldName, options.currentInstanceId);

  const fieldIndex = canvasData[options.currentInstanceId].additionalTexts.indexOf(fieldName);
  addFieldSettings({ fieldName, options, fieldIndex, prefix: ADDITIONAL_TEXT_PREFIX_ID });

  await drawFieldsFunctions.drawAdditionalText({canvas: options.currentPreview.canvas, id: options.currentInstanceId});
  const currentRenderedObject = options.currentPreview.canvas.getObjects().find(o => o.id === ADDITIONAL_TEXT_PREFIX_ID + (canvasData[options.currentInstanceId].additionalTexts.length - 1));
  orderHandler(currentRenderedObject, options, params);
  await reorderObjects(options.currentPreview.canvas);
  moveRulerGuideLinesToFront(options.currentPreview.canvas)
  
  return {fieldName};
}

export async function addMultiLineText(field_type, options = {}, params = {}) {
  const fieldName = getFieldName(field_type);
  const newData = {};
  newData[fieldName] = options.text || 'Text';
  
  if (options.name) {
    newData[fieldName + '_name'] = options.name;
  }
  
  //set default font by template params
  if (!options['_font']) {
    let defaultFont = 'Proxima-Nova-Semibold';
    let textFont = '';
    
    if (options.currentPreview.template && options.currentPreview.template?.fields && Array.isArray(options.currentPreview.template.fields)) {
      const fields = options.currentPreview.template.fields;
      let extraFont = '';
      for(let i = 0; i < fields.length; i++) {
        let opts = JSON.parse(fields[i].options);
        if (fields[i].element_id.includes('_text_') && opts?.Font) {
          textFont = opts.Font;
          break;
        }

        if (opts?.Font) {
          extraFont = opts.Font;
          break;
        }
      }

      if (!textFont) textFont = extraFont;
    }
    
    options['_font'] = textFont || defaultFont;
  }
  
  for (let key of Object.keys(DEFAULT_MULTI_LINE_TEXT_FIELD_PROPERTIES)) {
    newData[`${fieldName + key}`] = options[key] || DEFAULT_MULTI_LINE_TEXT_FIELD_PROPERTIES[key];
  }
  // for duplication
  if (options.isDuplicate) {
    newData[`${fieldName}`] = options.textLines ?? 'Text';
    // adding additional properties
  }
  

  canvasData[options.currentInstanceId].additionalMultiLineTexts.push(fieldName);

  const fieldIndex = canvasData[options.currentInstanceId].additionalMultiLineTexts.indexOf(fieldName);
  addFieldSettings({ fieldName, options, fieldIndex, prefix: ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID });

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  applyShadowProperties(options, fieldName, options.currentInstanceId);

  await drawFieldsFunctions.drawAdditionalMultiLineText({canvas: options.currentPreview.canvas, id: options.currentInstanceId});
  const currentRenderedObject = options.currentPreview.canvas.getObjects().find(o => o.id === ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID + (canvasData[options.currentInstanceId].additionalMultiLineTexts.length - 1));
  const glyphShift = currentRenderedObject && detectedTextTranslation(currentRenderedObject, options.visibleGlyphBox);
  if (glyphShift) {
    currentRenderedObject.set({left: currentRenderedObject.left + glyphShift.x, top: currentRenderedObject.top + glyphShift.y});
    currentRenderedObject.setCoords();
    updateLayoutChanges(options.currentInstanceId, fieldName + '_offset_x', Number(newData[fieldName + '_offset_x']) + glyphShift.x);
    updateLayoutChanges(options.currentInstanceId, fieldName + '_offset_y', Number(newData[fieldName + '_offset_y']) + glyphShift.y);
    options.currentPreview.canvas.requestRenderAll();
  }
  orderHandler(currentRenderedObject, options, params);
  await reorderObjects(options.currentPreview.canvas);
  moveRulerGuideLinesToFront(options.currentPreview.canvas)
  if (currentRenderedObject) options.currentPreview.canvas.setActiveObject(currentRenderedObject);

  if (
    options.startEditingInstanceId === options.currentInstanceId
    && currentRenderedObject?.editable
  ) {
    currentRenderedObject.enterEditing();
    currentRenderedObject.selectAll();
    options.currentPreview.canvas.requestRenderAll();
  }

  return {fieldName};
}

async function addRectangle(field_type, options = {}, params = {}) {
  const fieldName = getFieldName(field_type);
  const newData = {};
  newData[fieldName] = '#000';
  for (let key of Object.keys(DEFAULT_RECTANGLE_FIELD_PROPERTIES)) {
    newData[`${fieldName + key}`] = options[key] !== undefined ? options[key] : DEFAULT_RECTANGLE_FIELD_PROPERTIES[key];
  }
  canvasData[options.currentInstanceId].additionalRectangles.push(fieldName);

  const fieldIndex = canvasData[options.currentInstanceId].additionalRectangles.indexOf(fieldName);
  addFieldSettings({ fieldName, options, fieldIndex, prefix: ADDITIONAL_RECTANGLE_PREFIX_ID });

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  applyShadowProperties(options, fieldName, options.currentInstanceId);

  await drawFieldsFunctions.drawAdditionalRectangle({canvas: options.currentPreview.canvas, id: options.currentInstanceId});
  const currentRenderedObject = options.currentPreview.canvas.getObjects().find(o => o.id === ADDITIONAL_RECTANGLE_PREFIX_ID + (canvasData[options.currentInstanceId].additionalRectangles.length - 1));
  orderHandler(currentRenderedObject, options, params);
  await reorderObjects(options.currentPreview.canvas);
  moveRulerGuideLinesToFront(options.currentPreview.canvas)

  return {fieldName};
}

async function addCircle(field_type, options = {}, params = {}) {
  const fieldName = getFieldName(field_type);
  const newData = {};
  newData[fieldName] = '#000';
  for (let key of Object.keys(DEFAULT_CIRCLE_FIELD_PROPERTIES)) {
    newData[`${fieldName + key}`] = options[key] || DEFAULT_CIRCLE_FIELD_PROPERTIES[key];
  }
  canvasData[options.currentInstanceId].additionalCircles.push(fieldName);

  const fieldIndex = canvasData[options.currentInstanceId].additionalCircles.indexOf(fieldName);
  addFieldSettings({ fieldName, options, fieldIndex, prefix: ADDITIONAL_CIRCLE_PREFIX_ID });

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  applyShadowProperties(options, fieldName, options.currentInstanceId);

  await drawFieldsFunctions.drawAdditionalCircle({canvas: options.currentPreview.canvas, id: options.currentInstanceId});
  const currentRenderedObject = options.currentPreview.canvas.getObjects().find(o => o.id === ADDITIONAL_CIRCLE_PREFIX_ID + (canvasData[options.currentInstanceId].additionalCircles.length - 1));
  orderHandler(currentRenderedObject, options, params);
  await reorderObjects(options.currentPreview.canvas);
  moveRulerGuideLinesToFront(options.currentPreview.canvas)

  return {fieldName};
}

function extractPlainObject(data) {
  if (!data) return null;
  if (typeof data !== 'object') return data;
  try {
    return JSON.parse(JSON.stringify(data));
  } catch (e) {
    return data;
  }
}

function reInitializeAdditionalBackgroundThemeImage(canvasData, previewId) {
  canvasData[previewId].additional_background_theme_image = [];
  initAdditionalBackgroundThemeImage(canvasData, previewId)
}

export async function addBackgroundThemeImage(options = {}, params = {}) {
  const newData = {};
  const resolvedOrder = Number.isFinite(options.add_bk_img_order) ? options.add_bk_img_order : order;
  let properties = {
    ...DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES,
    add_bk_img_order: resolvedOrder,
  };

  
  if (Object.hasOwn(options, 'additional_background_images')) {
    // Determine scale values: prefer explicitly provided add_bk_img_scale values, then fall back to scaleX/scaleY
    let imgScale = DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_scale;
    let imgScaleY = DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_scale_y;

    if (options.add_bk_img_scale !== undefined && !isNaN(options.add_bk_img_scale)) {
      imgScale = options.add_bk_img_scale;
    } else if (!isNaN(options.scaleX) || !isNaN(options.scaleY)) {
      imgScale = Math.max(options.scaleX || 0, options.scaleY || 0) || imgScale;
    }

    if (options.add_bk_img_scale_y !== undefined && !isNaN(options.add_bk_img_scale_y)) {
      imgScaleY = options.add_bk_img_scale_y;
    } else if (!isNaN(options.scaleY)) {
      imgScaleY = options.scaleY;
    }

    const shadowData = options.shadow
      ? (options.shadow.toObject ? options.shadow.toObject() : extractPlainObject(options.shadow))
      : null;

    const mirrorShadowData = options.mirrorShadow ? extractPlainObject(options.mirrorShadow) : null;
    const ovalShadowData = options.ovalShadow ? extractPlainObject(options.ovalShadow) : null;

    properties = {
      ...properties,
      additional_background_images: cleanImageResourceUrl(options.additional_background_images),
      add_bk_img_offset_x: options.add_bk_img_offset_x ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_offset_x,
      add_bk_img_offset_y: options.add_bk_img_offset_y ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_offset_y,
      add_bk_img_scale: imgScale,
      add_bk_img_scale_y: imgScaleY,
      add_bk_img_height: options.height ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_height,
      add_bk_img_width: options.width ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_width,
      add_bk_img_is_paste_object: !!params.pasteObject,
      add_bk_img_is_another_instance: params.pasteObject ? options.currentInstanceId !== options.prevInstanceId : !!params.pasteObject,
      add_bk_img_is_shifted: !!params.pasteObject,
      add_bk_img_flipX: options.add_bk_img_flipX ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_flipX,
      add_bk_img_flipY: options.add_bk_img_flipY ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_flipY,
      add_bk_img_angle: options.add_bk_img_angle ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_angle,
      add_bk_img_opacity: options.add_bk_img_opacity ?? options.opacity ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_opacity,
      // Store paste scaled dimensions for proper size calculation in setAdditionalBackgroundImage
      add_bk_img_paste_scaled_width: options.pasteScaledWidth ?? null,
      add_bk_img_paste_scaled_height: options.pasteScaledHeight ?? null,
      add_bk_img_shadow: options.add_bk_img_shadow ?? (shadowData ? JSON.stringify(shadowData) : DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_shadow),
      add_bk_img_custom_shadow: shadowData ? JSON.stringify(shadowData) : (options.add_bk_img_custom_shadow ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_custom_shadow),
      add_bk_img_custom_outline_shadow: options.add_bk_img_custom_outline_shadow ?? DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.add_bk_img_custom_outline_shadow,
      add_bk_img_mirror_shadows: mirrorShadowData || [],
      add_bk_img_oval_shadows: ovalShadowData || [],
    }
  }

  const fieldIndex = canvasData[options.currentInstanceId].additional_background_theme_image.length;

  for (let key of Object.keys(properties)) {
    const currentValues = Array.isArray(canvasData[options.currentInstanceId]._data[key])
      ? canvasData[options.currentInstanceId]._data[key]
      : [];
    newData[key] = Array.from(
      {length: Math.max(currentValues.length, fieldIndex + 1)},
      (_, index) => index === fieldIndex ? properties[key] : currentValues[index] ?? null
    );
  }
  
  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  const elementId = 'additional-' + fieldIndex;

  if (properties.add_bk_img_custom_shadow) {
    updateLayoutChanges(options.currentInstanceId, `${elementId}_custom_shadow`, properties.add_bk_img_custom_shadow);
  }

  if (properties.add_bk_img_custom_outline_shadow) {
    updateLayoutChanges(options.currentInstanceId, `${elementId}_custom_outline_shadow`, properties.add_bk_img_custom_outline_shadow);
  }

  reInitializeAdditionalBackgroundThemeImage(canvasData, options.currentPreview.id);
  addFieldSettings({ options, fieldIndex, prefix: ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID });

  const currentPreview = previews.find(preview => preview.id === options.currentInstanceId);
  await setAdditionalBackgroundImage(
    {...currentPreview, isNeedReRenderCanvas: !params.deferRender},
    options
  );
  const currentRenderedObject = options.currentPreview.canvas.getObjects().find(o => o.id === ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID + (canvasData[options.currentInstanceId].additional_background_theme_image.length - 1));
  if ('deleteWhenUserClosesImageSelectionModal' in options && options.deleteWhenUserClosesImageSelectionModal === true ) {
    $('#selectBkImgModal').data('add-background-theme-image-object-id', currentRenderedObject.id);
  }
  orderHandler(currentRenderedObject, options, params);
  await reorderObjects(options.currentPreview.canvas);

  // Only auto-resize images that are too large when NOT pasting a copied object
  // When pasting, we want to preserve the original size
  if (currentRenderedObject && !params.pasteObject && !options.skipAutoResize) {
    const canvas = options.currentPreview.canvas;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const imageWidth = currentRenderedObject.width * currentRenderedObject.scaleX;
    const imageHeight = currentRenderedObject.height * currentRenderedObject.scaleY;

    if (imageWidth > canvasWidth || imageHeight > canvasHeight) {
      const aspectRatio = canvas.width / canvas.height;
      const sizeKey = aspectRatio < 1 ? 'width' : 'height';

      const zoom = canvas.getZoom();

      const paddingFactor = 0.5;
      const templateSize = (canvas[sizeKey] / zoom) * paddingFactor;
      const scaleByAspectRatio = templateSize / currentRenderedObject[sizeKey];

      const xCalc = ((scaleByAspectRatio - currentRenderedObject.scaleX) * currentRenderedObject.width) / 2;
      const yCalc = ((scaleByAspectRatio - currentRenderedObject.scaleY) * currentRenderedObject.height) / 2;

      currentRenderedObject.set({
        top: currentRenderedObject.top + yCalc,
        left: currentRenderedObject.left + xCalc,
        scaleX: templateSize / currentRenderedObject[sizeKey],
        scaleY: templateSize / currentRenderedObject[sizeKey],
      });

      const centerPoint = canvas.getCenter();
      const newLeft = centerPoint.left / zoom;
      const newTop = centerPoint.top / zoom;

      currentRenderedObject.set({
        left: newLeft,
        top: newTop
      });

      currentRenderedObject.setCoords();

      const index = currentRenderedObject.id.split('_').pop();
      const addBkImageScaleX = [...options.currentPreview.data.add_bk_img_scale];
      const addBkImageScaleY = [...options.currentPreview.data.add_bk_img_scale_y];
      let offsetXList = options.currentPreview.data.add_bk_img_offset_x ?? [];
      let offsetYList = options.currentPreview.data.add_bk_img_offset_y ?? [];
      const addBkImageOffsetX = [...offsetXList];
      const addBkImageOffsetY = [...offsetYList];

      addBkImageScaleX[index] = currentRenderedObject.scaleX;
      addBkImageScaleY[index] = currentRenderedObject.scaleY;
      addBkImageOffsetX[index] = currentRenderedObject.left;
      addBkImageOffsetY[index] = currentRenderedObject.top;

      updateLayoutChanges(options.currentInstanceId, 'add_bk_img_scale', addBkImageScaleX);
      updateLayoutChanges(options.currentInstanceId, 'add_bk_img_scale_y', addBkImageScaleY);
      updateLayoutChanges(options.currentInstanceId, 'add_bk_img_offset_x', addBkImageOffsetX);
      updateLayoutChanges(options.currentInstanceId, 'add_bk_img_offset_y', addBkImageOffsetY);
    }
  }

  if (params.pasteObject && options.realScaleX) {
    const preview = previews.find(preview => preview.id === options.currentInstanceId);
    const isObjectTransfer = Boolean(options.isObjectTransfer || options.transferTargetTopRight);
    const sourceTemplateWidth = options.sourceTemplateWidth;
    const sourceTemplateHeight = options.sourceTemplateHeight;
    const targetTemplateWidth = preview.canvas.width;
    const targetTemplateHeight = preview.canvas.height;
    let scaleFactor = 1;

    const imageWidth = options.width * options.realScaleX;
    const imageHeight = options.height * options.realScaleY;

    if (!isObjectTransfer && sourceTemplateWidth && sourceTemplateHeight && targetTemplateWidth && targetTemplateHeight) {
      const sourceAspectRatio = sourceTemplateWidth / sourceTemplateHeight;
      const targetAspectRatio = targetTemplateWidth / targetTemplateHeight;

      if (sourceAspectRatio > targetAspectRatio) {
        scaleFactor = targetTemplateWidth / sourceTemplateWidth;
      } else {
        scaleFactor = targetTemplateHeight / sourceTemplateHeight;
      }
    }

    if (!(options.pasteScaledWidth && Math.abs(currentRenderedObject.getScaledWidth() - options.pasteScaledWidth) < 1)) {
      currentRenderedObject.scaleToWidth(imageWidth * scaleFactor);
    }
    if (!isObjectTransfer) {
      currentRenderedObject.set({
        left: currentRenderedObject.left * scaleFactor,
        top: currentRenderedObject.top * scaleFactor,
      });
    }
    currentRenderedObject.setCoords();
    
    const addBkImageScaleX = [...options.currentPreview.data.add_bk_img_scale];
    const addBkImageScaleY = [...options.currentPreview.data.add_bk_img_scale_y];
    const addBkImageWidth = [...options.currentPreview.data.add_bk_img_width];
    const addBkImageHeight = [...options.currentPreview.data.add_bk_img_height];
    const addBkImageOffsetX = [...options.currentPreview.data.add_bk_img_offset_x];
    const addBkImageOffsetY = [...options.currentPreview.data.add_bk_img_offset_y];
    
    const addBkImageIsPasteObject = [...options.currentPreview.data.add_bk_img_is_paste_object];
    const addBkImageDuplicateData = [...options.currentPreview.data.add_bk_img_duplicate_data];
    const index = currentRenderedObject.id.split('_').pop();
    const {canvas_data} = getShowPageScope();
    const shouldPersistVisualPasteSize = Boolean(options.pasteScaledWidth && options.pasteScaledHeight);

    if (shouldPersistVisualPasteSize) {
      addBkImageScaleX[index] = 1;
      addBkImageScaleY[index] = 1;
    } else if (!options.isOriginalScale) {
      addBkImageScaleX[index] = currentRenderedObject.scaleX;
      addBkImageScaleY[index] = currentRenderedObject.scaleY;
    } else {
      addBkImageScaleX[index] = options.scaleX * scaleFactor;
      addBkImageScaleY[index] = options.scaleY * scaleFactor;
    }
    
    addBkImageOffsetX[index] = currentRenderedObject.left;
    addBkImageOffsetY[index] = currentRenderedObject.top;
    
    addBkImageIsPasteObject[index] = !shouldPersistVisualPasteSize;

    if (shouldPersistVisualPasteSize) {
      addBkImageWidth[index] = currentRenderedObject.getScaledWidth();
      addBkImageHeight[index] = currentRenderedObject.getScaledHeight();
      addBkImageDuplicateData[index] = JSON.stringify({
        width: addBkImageWidth[index],
        height: addBkImageHeight[index],
      });
    } else if (!options.isOriginalScale) {
      addBkImageWidth[index] = currentRenderedObject.width;
      addBkImageHeight[index] = currentRenderedObject.height;
    } else {
      addBkImageWidth[index] = options.width;
      addBkImageHeight[index] = options.height;
    }
    canvas_data[options.currentPreview.id].additionalBkImgCoords[index]["scaleX"] = 1;
    canvas_data[options.currentPreview.id].additionalBkImgCoords[index]["scaleY"] = 1;
    
    
    // canvas_data[options.currentPreview.id][].add_bk_img_offset_x[index] = currentRenderedObject.left * scaleFactor;
    // canvas_data[options.currentPreview.id].add_bk_img_offset_y[index] = currentRenderedObject.top * scaleFactor;
    // canvas_data[options.currentPreview.id].add_bk_img_width[index] = currentRenderedObject.width;
    // canvas_data[options.currentPreview.id].add_bk_img_height[index] = currentRenderedObject.height;
    // canvas_data[options.currentPreview.id].add_bk_img_scale[index] = currentRenderedObject.scaleX;
    // canvas_data[options.currentPreview.id].add_bk_img_scale_y[index] = currentRenderedObject.scaleY;
    
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_scale', addBkImageScaleX);
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_scale_y', addBkImageScaleY);
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_width', addBkImageWidth);
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_height', addBkImageHeight);
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_duplicate_data', addBkImageDuplicateData);
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_offset_x', addBkImageOffsetX);
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_offset_y', addBkImageOffsetY);
    updateLayoutChanges(options.currentInstanceId, 'add_bk_img_is_paste_object', addBkImageIsPasteObject);
  }

  if (params.pasteObject && options.transferTargetTopRight && currentRenderedObject) {
    currentRenderedObject.setCoords();
    const topRight = currentRenderedObject.getCoords?.()[1];

    if (topRight) {
      const targetTopRight = options.transferTargetTopRight;
      currentRenderedObject.set({
        left: currentRenderedObject.left + (targetTopRight.x - topRight.x),
        top: currentRenderedObject.top + (targetTopRight.y - topRight.y),
      });
      currentRenderedObject.setCoords();

      const index = currentRenderedObject.id.split('_').pop();
      const addBkImageOffsetX = [...options.currentPreview.data.add_bk_img_offset_x];
      const addBkImageOffsetY = [...options.currentPreview.data.add_bk_img_offset_y];

      addBkImageOffsetX[index] = currentRenderedObject.left;
      addBkImageOffsetY[index] = currentRenderedObject.top;

      updateLayoutChanges(options.currentInstanceId, 'add_bk_img_offset_x', addBkImageOffsetX);
      updateLayoutChanges(options.currentInstanceId, 'add_bk_img_offset_y', addBkImageOffsetY);
    }
  }

  if (!params.deferRender) {
    options.currentPreview.canvas.renderAll();
  }
  moveRulerGuideLinesToFront(options.currentPreview.canvas)

  return {fieldName: currentRenderedObject?.element_id};
}

async function addSafeZone(fieldType, options = {}, params = {}) {
  const fieldName = getFieldName(fieldType);
  const newData = {};
  newData[fieldName] = '#000';
  for (let key of Object.keys(DEFAULT_SAFE_ZONE_FIELD_PROPERTIES)) {
    newData[`${fieldName + key}`] = options[key] || DEFAULT_SAFE_ZONE_FIELD_PROPERTIES[key];
  }
  canvasData[options.currentInstanceId].additionalSafeZones.push(fieldName);

  const fieldIndex = canvasData[options.currentInstanceId].additionalSafeZones.indexOf(fieldName);
  addFieldSettings({ fieldName, options, fieldIndex, prefix: ADDITIONAL_SAFE_ZONE_PREFIX_ID });

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }
  await drawFieldsFunctions.drawAdditionalSafeZone({canvas: options.currentPreview.canvas, id: options.currentInstanceId});
  const currentRenderedObject = options.currentPreview.canvas.getObjects().find(o => o.id === ADDITIONAL_SAFE_ZONE_PREFIX_ID + (canvasData[options.currentInstanceId].additionalSafeZones.length - 1));
  orderHandler(currentRenderedObject, options, params);
  await reorderObjects(options.currentPreview.canvas);
  moveRulerGuideLinesToFront(options.currentPreview.canvas)

  return {fieldName};
}
 
let handleSaveIcons = null;

async function addIcons(field_type, options = {}, params = {}) {
  iconsSettingsEventBroker.fire(EVENTS.INIT, {
    isShowPage: true,
    data: {
      urls: [''],
      iconsGrid: 'disabled',
      iconsInRow: 2,
      iconsRows: 2,
      horizontalOffset: 20,
      verticalOffset: 20,
    }
  });
  
  if (handleSaveIcons) {
    iconsSettingsEventBroker.off(EVENTS.SAVE, handleSaveIcons);
  }

  handleSaveIcons = async ({
    urls,
    top,
    left,
    width,
    height,
    placementOrientation,
    placementOrder,
    iconsGrid,
    iconsInRow,
    iconsRows,
    horizontalOffset,
    verticalOffset,
  }) => {
    if (!urls.length) return;
    
    const canvas = options.currentPreview.canvas;
      
    const additionalIcons = Object.hasOwn(canvasData[options.currentInstanceId]._data, 'add_icons')
      ? [...canvasData[options.currentInstanceId]._data['add_icons']]
      : [];

    const icons = {
      id: uuid(),
      elements: urls.map(url => ({
        ...DEFAULT_ICONS_FIELD_PROPERTIES, 
        top,
        left,
        width,
        height,
        url
      })),
      placementOrientation,
      placementOrder,
      iconsGrid,
      iconsInRow,
      iconsRows,
      horizontalOffset,
      verticalOffset,
    }

    additionalIcons.push(icons);
    
    updateLayoutChanges(options.currentInstanceId, 'add_icons', additionalIcons);
    
    await drawFieldsFunctions.drawAdditionalIcons({
      canvas,  
      data: canvasData[options.currentInstanceId]._data 
    });
    
    await reorderObjects(options.currentPreview.canvas);
    
    canvas.renderAll();
    
    moveRulerGuideLinesToFront(options.currentPreview.canvas);
  }
  
  iconsSettingsEventBroker.on(EVENTS.SAVE, handleSaveIcons);

  return {fieldName: null};
}

async function addLine(field_type, options = {}, params = {}) {
  const fieldName = getFieldName(field_type);
  const newData = {};
  
  newData[fieldName] = '#000';
  for (let key of Object.keys(DEFAULT_LINE_FIELD_PROPERTIES)) {
    newData[`${fieldName + key}`] = options[key] || DEFAULT_LINE_FIELD_PROPERTIES[key];
  }
  if (
    options._width !== null
    && options._width !== undefined
    && options._width !== ''
    && Number.isFinite(Number(options._width))
  ) {
    newData[`${fieldName}_width`] = Number(options._width);
  }

  const lineCoords = newData[`${fieldName}_coords`];
  const suppliedPsdWidth = Number(options._psd_width);
  const lineThickness = Number(newData[`${fieldName}_thickness`]);
  const hasSuppliedPsdWidth = options._psd_width !== null
    && options._psd_width !== undefined
    && options._psd_width !== ''
    && Number.isFinite(suppliedPsdWidth);
  if (hasSuppliedPsdWidth) {
    newData[`${fieldName}_psd_width`] = suppliedPsdWidth;
  } else if (
    Array.isArray(lineCoords)
    && lineCoords.length === 4
    && lineCoords.every(coord => Number.isFinite(Number(coord)))
    && Number.isFinite(lineThickness)
  ) {
    newData[`${fieldName}_psd_width`] = Math.hypot(
      Number(lineCoords[2]) - Number(lineCoords[0]),
      Number(lineCoords[3]) - Number(lineCoords[1]),
    ) + lineThickness;
  }
  
  canvasData[options.currentInstanceId].additionalLines.push(fieldName);

  const fieldIndex = canvasData[options.currentInstanceId].additionalLines.indexOf(fieldName);
  addFieldSettings({ fieldName, options, fieldIndex, prefix: ADDITIONAL_LINE_PREFIX_ID });

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  applyShadowProperties(options, fieldName, options.currentInstanceId);

  await drawFieldsFunctions.drawAdditionalLine({
    canvas: options.currentPreview.canvas,
    id: options.currentInstanceId
  });

  const currentRenderedObject = options.currentPreview.canvas
    .getObjects()
    .find(o =>
      o.id === `${ADDITIONAL_LINE_PREFIX_ID}${(canvasData[options.currentInstanceId].additionalLines.length - 1)}`
    );

  orderHandler(currentRenderedObject, options, params);
  await reorderObjects(options.currentPreview.canvas);
  moveRulerGuideLinesToFront(options.currentPreview.canvas);

  return {fieldName};
}

let handleCreateAdditionalTableField = null;

async function addTable(field_type, options) {
  const canvas = options.currentPreview.canvas;

  const table = new BuilderTable.Table();

  table.styles.setRules({
    'font-family': 'Avenir-Black',
    'font-size': '25px',
  });

  table.fieldOptions = { X: 0, Y: 0 };

  simpleTableBuilderEventBroker.fire(Table_EVENTS.INIT, { table, isAdditionalTableField: true });

  if (handleCreateAdditionalTableField) {
    simpleTableBuilderEventBroker.off(Table_EVENTS.ADD_ADDITIONAL_TABLE_FIELD, handleCreateAdditionalTableField);
  }

  handleCreateAdditionalTableField = async ({ tableInstance }) => {
    let additionalTables = Object.hasOwn(canvasData[options.currentInstanceId]._data, 'add_html_tables')
      ? [...canvasData[options.currentInstanceId]._data['add_html_tables']]
      : [];

    additionalTables.push(JSON.stringify(tableInstance));

    updateLayoutChanges(options.currentInstanceId, 'add_html_tables', additionalTables);

    await drawFieldsFunctions.drawAdditionalHtmlTable({
      canvas,
      data: canvasData[options.currentInstanceId]._data
    });

    await reorderObjects(options.currentPreview.canvas);

    moveRulerGuideLinesToFront(options.currentPreview.canvas);
  }

  simpleTableBuilderEventBroker.on(Table_EVENTS.ADD_ADDITIONAL_TABLE_FIELD, handleCreateAdditionalTableField);

  return {fieldName: null};
}

// Function to paste a copied table directly (without opening the modal)
export async function pasteTable(options = {}, params = {}) {
  const canvas = options.currentPreview.canvas;

  // Get the table data from the copied object
  const tableData = options.tableData;
  const htmlTableData = options.html_table_data;

  if (!tableData && !htmlTableData) {
    console.warn('No table data found to paste');
    return;
  }

  let additionalTables = Object.hasOwn(canvasData[options.currentInstanceId]._data, 'add_html_tables')
    ? [...canvasData[options.currentInstanceId]._data['add_html_tables']]
    : [];

  // Parse the table data and update position if needed
  let tableDataToPaste = tableData;
  if (typeof tableDataToPaste === 'string') {
    try {
      tableDataToPaste = JSON.parse(tableDataToPaste);
    } catch (e) {
      console.error('Error parsing table data:', e);
    }
  }

  // Update position and angle if provided
  if (options._offset_x !== undefined || options._offset_y !== undefined || options._angle !== undefined) {
    tableDataToPaste = {
      ...tableDataToPaste,
      X: options._offset_x ?? tableDataToPaste.X ?? 0,
      Y: options._offset_y ?? tableDataToPaste.Y ?? 0,
      Angle: options._angle ?? tableDataToPaste.Angle ?? 0,
    };
  }

  // Find the original table data from html_table_data array and modify its fieldOptions
  if (htmlTableData && Array.isArray(htmlTableData)) {
    // Find the table that matches this field_id or use the first one
    const originalTableIndex = htmlTableData.findIndex(t => {
      const parsed = typeof t === 'string' ? JSON.parse(t) : t;
      return parsed.id === options.field_id;
    });

    const tableIndex = originalTableIndex >= 0 ? originalTableIndex : 0;
    let originalTable = htmlTableData[tableIndex];

    if (typeof originalTable === 'string') {
      originalTable = JSON.parse(originalTable);
    }

    // Update the position and angle in fieldOptions
    const fieldOptions = typeof originalTable.fieldOptions === 'string'
      ? JSON.parse(originalTable.fieldOptions)
      : originalTable.fieldOptions;

    fieldOptions.X = options._offset_x ?? fieldOptions.X ?? 0;
    fieldOptions.Y = options._offset_y ?? fieldOptions.Y ?? 0;
    fieldOptions.Angle = options._angle ?? fieldOptions.Angle ?? 0;

    originalTable.fieldOptions = typeof originalTable.fieldOptions === 'string'
      ? JSON.stringify(fieldOptions)
      : fieldOptions;

    additionalTables.push(JSON.stringify(originalTable));
  }

  updateLayoutChanges(options.currentInstanceId, 'add_html_tables', additionalTables);

  await drawFieldsFunctions.drawAdditionalHtmlTable({
    canvas,
    data: canvasData[options.currentInstanceId]._data
  });

  await reorderObjects(options.currentPreview.canvas);

  moveRulerGuideLinesToFront(options.currentPreview.canvas);
}

async function addSmartObject(type, options, params = {}) {
  const canvas = options.currentPreview.canvas;
  
  const canvasSmartObjects = canvas
    .getObjects()
    .filter(o => o.templateField?.type === FIELD_TYPE_SMART_OBJECT)
    .map(o => ({
      element_id: o.element_id,
      groupName: o.groupName,
    }));

  const canvasSet = new Set(canvasSmartObjects);

  const deletedSmartObjects = options.currentPreview.template.fields
    .filter(f =>
      f.type === FIELD_TYPE_SMART_OBJECT &&
      !canvasSet.has(f.element_id)
    )
    .map(f => ({
      element_id: f.element_id,
      groupName: JSON.parse(f.options)['Group Name'],
    }));

  const allSmartObjects = [
    ...canvasSmartObjects,
    ...deletedSmartObjects
  ];
  
  const nextSmartObjectData = getNextSmartObjectData(allSmartObjects);
  const elementId = nextSmartObjectData.id;
  const groupName = nextSmartObjectData.groupName;
  
  const newData = {};
  options['_group_name'] = groupName;

  for (let key of Object.keys(DEFAULT_SMART_OBJECT_FIELD_PROPERTIES)) {
    newData[`${elementId + key}`] = hasOwnOption(options, key)
      ? options[key]
      : DEFAULT_SMART_OBJECT_FIELD_PROPERTIES[key];
  }

  options.X = parseInt(newData[`${elementId}_x`]);
  options.Y = parseInt(newData[`${elementId}_y`]);

  canvasData[options.currentInstanceId].smartObjCoords[groupName] = { ...options };
  canvasData[options.currentInstanceId].additionalSmartObjects.push(elementId);

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  const preview = previews.find(preview => preview.id === options.currentInstanceId)
  const { template } = preview;
  const { fields } = template;

  await drawFieldsFunctions.drawSmartObject({
    fields,
    canvas,
    data: canvasData[options.currentInstanceId]._data,
    users_fields_order: options.users_fields_order,
  });
  
  await reorderObjects(options.currentPreview.canvas);

  const currentRenderedObject = options.currentPreview.canvas.getObjects().find(o => o.element_id === elementId);
  orderHandler(currentRenderedObject, options, params);

  if (currentRenderedObject && hasOwnOption(options, 'convertedMemberId')) {
    const hasConvertedMemberId = options.convertedMemberId != null;

    if (!hasConvertedMemberId) {
      console.warn('Missing convertedMemberId for SmartObject conversion.');
    } else {
      const convertedMemberId = String(options.convertedMemberId);
      const canvasObjects = canvas.getObjects();
      const convertedObject = canvasObjects.find(obj => String(obj.fieldId != null ? obj.fieldId : obj.id) === convertedMemberId);

      if (convertedObject) {
        currentRenderedObject.options = {
          ...currentRenderedObject.options,
          preserveMemberPositionOnTransform: true,
        };

        convertedObject.set({
          groupName: currentRenderedObject.groupName,
          clipPath: currentRenderedObject.clipPathArea,
          smartObject: currentRenderedObject,
        });

        if (!currentRenderedObject.members.includes(convertedMemberId)) {
          currentRenderedObject.members.push(convertedMemberId);
        }

        normalizeConvertedProductImageSmartObjectOffsets({
          convertedObject,
          currentRenderedObject,
          currentInstanceId: options.currentInstanceId,
        });

        currentRenderedObject.updateChanges();
      }
    }

    canvas.setActiveObject(currentRenderedObject);
  }
  
  canvas.renderAll();

  moveRulerGuideLinesToFront(options.currentPreview.canvas);

  setTimeout(() => {
    canvas.fire('show:smart-object-editor', {instanceId: options.currentInstanceId, preview: preview, smartObjectId: elementId});
  }, 500)
  
  return {fieldName: elementId};
}

async function addCutLine(type, options) {
  const fieldName = getFieldName(type);
  const newData = {};
  newData[fieldName] = '#000';
  newData[`${fieldName + '_offset_y'}`] = canvasData[options.currentInstanceId].templateInstance.height / 2;
  newData[`${fieldName + '_coords'}`] = [0, 0, canvasData[options.currentInstanceId].templateInstance.width, 0];
  for (let key of Object.keys(DEFAULT_CUT_LINE_PROPERTIES)) {
    newData[`${fieldName + key}`] = options[key] || DEFAULT_CUT_LINE_PROPERTIES[key];
  }
  canvasData[options.currentInstanceId].additionalCutLines.push(fieldName);

  const fieldIndex = canvasData[options.currentInstanceId].additionalCutLines.indexOf(fieldName);
  addFieldSettings({ fieldName, options, fieldIndex, prefix: ADDITIONAL_CUT_LINE_PREFIX_ID });

  for (let key of Object.keys(newData)) {
    updateLayoutChanges(options.currentInstanceId, key, newData[key]);
  }

  await drawFieldsFunctions.drawAdditionalCutLine({
    canvas: options.currentPreview.canvas,
    id: options.currentInstanceId
  });

  return {fieldName};
}

export async function addAddField(type, instanceId, options = {}, params = {}) {
  options.currentInstanceId = instanceId;
  // Allow caller to provide currentPreview directly (e.g. during early initialization)
  if (!options.currentPreview) {
    options.currentPreview = getPreview().find(preview => preview.id === options.currentInstanceId);
  }

  if (!type || !options.currentInstanceId || !options.currentPreview) return;
  if (params.addToComp) applyCompDefaults(type, options);
  let currentOrder = [];

  if (!options.currentPreview.data?.users_fields_order) {
    currentOrder = getAllObjects(options.currentPreview.canvas.getObjects()).objects
      .filter(
        object => object.id && 
          !NON_USER_LAYER_IDS.includes(object.id) && 
          !NON_USER_LAYER_IDS.some(id => object.id.startsWith(id))
      )
      .sort((prev, curr) => prev.order - curr.order)
      .map(object => object.fieldId || object.id);
    updateLayoutChanges(options.currentInstanceId, 'users_fields_order', JSON.stringify(currentOrder));
  }
  const fields = options.currentPreview.template.fields;
  const additionalFields = options.currentPreview.canvas
    .getObjects()
    .filter(obj => obj.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID));

  order = getOrderForAddField([
    ...fields,
    ...additionalFields
  ]);

  switch (type) {
    case 'Text':
      return await addAdditionalText(type, options, { ...params });
    case 'Multi Line Text':
      return await addMultiLineText(type, options, { ...params, hasUsersOrder: true });
    case 'Rectangle':
      return await addRectangle(type, options, { ...params, hasUsersOrder: true });
    case 'Circle':
      return await addCircle(type, options, { ...params, hasUsersOrder: true });
    case 'Background Theme Image':
      return await addBackgroundThemeImage(options, { ...params, hasUsersOrder: true });
    case 'Safe Zone':
      return await addSafeZone(type, options, params)
    case 'Icons':
      return await addIcons(type, options, params);
    case 'Line':
      return await addLine(type, options, { ...params, hasUsersOrder: true });
    case 'Table':
      return await addTable(type, options, params);
    case 'Smart Object':
      return await addSmartObject(type, options, params);
    case 'Cut Line':
      return await addCutLine(type, options)
  }

  const isAdditionalFieldsExists = options.currentPreview.template.fields.find(({ type }) => type === FIELD_TYPE_ADDITIONAL_FIELDS)

  if (!isAdditionalFieldsExists) {
    const additionalField = (await TemplateService.createAdditionalFieldRow(options.currentPreview.template.id)).data
    options.currentPreview.template.fields.push(additionalField)
    const { drawPreviews, previews } = getShowPageScope()
    drawPreviews(previews)
  }
}

async function onAddField() {
  const type = $(this).attr('value');
  const instanceId = $(this).parent().data('id');

  // Setting the keepSelected variable to false allows the text fields to be unselected when an additional field is added
  canvasEditModeControl.setKeepSelected(false);

  const preview = getPreview().find(item => item.id === instanceId);
  const addToComp = preview?.canvas._compSelectedRegion === 'comp';
  const added = await addAddField(type, instanceId, {}, { addToComp });
  if (added?.fieldName) {
    preview?.canvas.fire('comp:field-added', { ...added, type, addToComp });
  }

  canvasEditModeControl.setKeepSelected(true);
}

export function initAdditionalFields(_canvas_data, _previews, _drawFieldsFunctions, _updateLayoutChanges, _reorderObjects) {
  canvasData = _canvas_data;
  previews = _previews;
  reorderObjects = _reorderObjects;
  drawFieldsFunctions = _drawFieldsFunctions;
  updateLayoutChanges = _updateLayoutChanges;
  $('.additional-fields-dropdown-list li').on('click', onAddField);
}
